import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { UndeliverableNumber } from "../types";
import { inboxApi } from "./api";
import { inboxKeys } from "./hooks";
import { useInboxToken } from "./useInboxToken";
import { buildUndeliverableMap, toE164 } from "shared/utils/sms-undeliverable";

const MAX_PHONE_NUMBERS = 500;
const EMPTY = new Map<string, UndeliverableNumber>();
// communication-service rejects the whole batch if one number fails this check.
const E164 = /^\+[1-9]\d{1,14}$/;

export const toRequestNumbers = (phones: (string | null | undefined)[]) =>
  [...new Set(phones.map(toE164).filter((phone) => E164.test(phone)))]
    .sort()
    .slice(0, MAX_PHONE_NUMBERS);

export const useUndeliverableNumbers = (
  phones: (string | null | undefined)[]
): Map<string, UndeliverableNumber> => {
  const { accessToken } = useInboxToken();
  const phoneNumbers = useMemo(() => toRequestNumbers(phones), [phones]);

  const { data } = useQuery({
    queryKey: inboxKeys.undeliverableStatus(phoneNumbers),
    queryFn: () => inboxApi.getUndeliverableStatus(phoneNumbers),
    enabled: phoneNumbers.length > 0 && !!accessToken,
    retry: 1
  });

  return useMemo(
    () => (data ? buildUndeliverableMap(data.undeliverable_numbers) : EMPTY),
    [data]
  );
};
